import { api } from '../../shared/api/client'

/** Система налогообложения кабинета — значения ENUM mp_sellers.tax_system. */
export type SellerTaxSystem = 'usn_income' | 'usn_income_expense' | 'ausn_income' | 'ausn_income_expense'

export interface Seller {
  seller_id: number
  seller_name: string
  seller_short_name: string
  marketplace: string
  user_id: number
  ozon_client_id?: number
  /** Я.Маркет: ID кабинета (business) — обязателен для marketplace === 'yandex_market' */
  ym_business_id?: number
  /** Я.Маркет: ID магазина (campaign) внутри кабинета — опционален */
  ym_campaign_id?: number
  token_expire_date: string
  active?: boolean
  brand?: string
  nds?: number
  live_exchange: boolean
  /** Склейка карточек кабинета по паре «тематика + категория» */
  cards_merge_enabled?: boolean
  cabinet_code?: string | null
  visual_path_token?: string | null
  /** Есть сохранённый токен кабинета — сам токен (шифроконверт) наружу не отдаётся */
  has_token?: boolean
  /** Система налогообложения кабинета (P&L и юнит-экономика аналитики); NULL — УСН «Доходы» */
  tax_system?: SellerTaxSystem | null
  /** Ставка налога, %; NULL — по системе (6/15/8/20). DECIMAL из MySQL приходит строкой */
  tax_rate?: number | string | null
  /** Client ID OZON Performance API (реклама OZON) — не путать с ozon_client_id Seller API */
  ozon_performance_client_id?: string | null
  /** Ключи OZON Performance API заданы; сам секрет наружу не отдаётся */
  ozon_performance_configured?: boolean
}

/** Поля настроек кабинета для аналитики, общие для создания и обновления. */
export type SellerAnalyticsSettingsInput = {
  /** Система налогообложения; null — не указана (УСН «Доходы») */
  tax_system?: SellerTaxSystem | null
  /** Ставка налога, %; null — по системе */
  tax_rate?: number | null
  ozon_performance_client_id?: string | null
  /** Новый Client Secret; пусто — не менять сохранённый */
  ozon_performance_secret?: string
}

export type UpdateSellerInput = Partial<Omit<Seller, 'seller_id' | 'tax_system' | 'tax_rate' | 'ozon_performance_client_id'>> &
  SellerAnalyticsSettingsInput & {
  seller_token?: string
  token_validity_days?: number
  /** true — удалить ключи OZON Performance API (Client ID и секрет) */
  clearOzonPerformance?: boolean
}

/**
 * Тело создания продавца. `cabinet_code` обязателен для всех площадок —
 * машинный ключ `(marketplace, cabinet_code)` не может быть пустым, иначе
 * перенос между стендами отменяет пакеты целиком (см. комментарий у
 * `SellerFilters.includeTokenless` в `seller-service/src/services/seller.service.ts`).
 * `& Record<string, unknown>` — форма создания в Sellers.tsx собирает это тело
 * спредом состояния формы (там есть служебные поля вроде `brands`), которые в
 * запрос не идут, но не должны валить проверку лишних свойств.
 */
export type CreateSellerInput = {
  seller_name: string
  seller_short_name: string
  marketplace: string
  seller_token: string
  brand: string
  cabinet_code: string
  ozon_client_id?: number
  ym_business_id?: number
  ym_campaign_id?: number
  token_expire_date?: string
  nds?: number
} & SellerAnalyticsSettingsInput & Record<string, unknown>

/** Один магазин (кампания) кабинета Я.Маркет из ответа проверки ключа. */
export interface YmVerifyCampaign {
  campaign_id: number
  name: string
  placement_type?: string
  business_id: number
  business_name?: string
}

export interface YmVerifyApiKeyResponse {
  ok: boolean
  campaigns?: YmVerifyCampaign[]
  error?: string
}

export const sellersAPI = {
  getSellers: (params?: any) =>
    api.get('/sellers', { params }),
  getSeller: (id: number) =>
    api.get(`/sellers/${id}`),
  createSeller: (sellerData: CreateSellerInput) =>
    api.post('/sellers', sellerData),
  updateSeller: (id: number, sellerData: UpdateSellerInput) =>
    api.put(`/sellers/${id}`, sellerData),
  deleteSeller: (id: number) =>
    api.delete(`/sellers/${id}`),
  /** Проверка Api-Key Я.Маркет: `POST /v2/auth` + `GET /v2/campaigns` на стороне сервиса-транспорта. */
  verifyYandexMarketApiKey: (apiKey: string) =>
    api.post<YmVerifyApiKeyResponse>('/yandex-market/verify-api-key', { api_key: apiKey }),
}
